/**
 * EditDecimalPlacesDialog Component
 * 
 * Dialog for editing currency decimal places.
 */

import React, { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Stack,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Currency } from "../../../types";

export interface EditDecimalPlacesDialogProps {
  open: boolean;
  onClose: () => void;
  onUpdate: () => Promise<void>;
  editingCurrency: Currency | null;
  decimalPlaces: number;
  setDecimalPlaces: (value: number) => void;
  updating: boolean;
}

/**
 * Dialog for editing currency decimal places
 */
export default function EditDecimalPlacesDialog({
  open,
  onClose,
  onUpdate,
  editingCurrency,
  decimalPlaces,
  setDecimalPlaces,
  updating,
}: EditDecimalPlacesDialogProps): React.ReactElement {
  // Sync decimalPlaces when dialog opens or currency changes
  // Only update when dialog opens or currency changes, not when decimalPlaces changes
  useEffect(() => {
    if (open && editingCurrency) {
      const currencyDecimalPlaces = editingCurrency.decimalPlaces ?? 2;
      setDecimalPlaces(currencyDecimalPlaces);
    }
  }, [open, editingCurrency, setDecimalPlaces]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">
            Edit Decimal Places - {editingCurrency?.code}
          </Typography>
          <IconButton size="small" onClick={onClose} disabled={updating}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            Select the number of decimal places to display for this currency.
          </Alert>
          <FormControl fullWidth>
            <InputLabel>Decimal Places</InputLabel>
            <Select
              value={decimalPlaces}
              onChange={(e) => {
                const newValue = Number(e.target.value);
                setDecimalPlaces(newValue);
              }}
              label="Decimal Places"
              disabled={updating}
            >
              <MenuItem value={0}>0 (None) - e.g., 1000</MenuItem>
              <MenuItem value={1}>1 - e.g., 1.0</MenuItem>
              <MenuItem value={2}>2 - e.g., 1.00</MenuItem>
              <MenuItem value={3}>3 - e.g., 1.000</MenuItem>
              <MenuItem value={4}>4 - e.g., 1.0000</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ p: 2, bgcolor: "background.default", borderRadius: 1, border: 1, borderColor: "divider" }}>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              Preview:
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {(1).toFixed(decimalPlaces)}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, pt: 1, gap: 1, display: "flex" }}>
        <Button 
          onClick={onClose}
          disabled={updating}
          variant="outlined"
          sx={{ flex: 1, py: 1.5 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onUpdate}
          disabled={updating}
          sx={{ flex: 1, py: 1.5 }}
        >
          {updating ? "Updating..." : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
